import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  type AppManifest,
  type AppPackage,
  type AppScan,
  type AppSummary,
  getAppCatalog,
  isAppDataTool,
  publishApp,
  validateApp,
} from "../../api/hub/apps";
import { HTML_LIMIT } from "./security";
const blank: AppManifest = {
  name: "",
  description: "",
  version: "1.0.0",
  icon: "🧩",
  tools: [],
  csp: { connectDomains: [], resourceDomains: [] },
  permissions: [],
};
export function UploadPanel(
  { app, onSaved, onClose }: {
    app?: AppSummary;
    onSaved: (app: AppSummary) => void;
    onClose: () => void;
  },
) {
  const [html, setHtml] = useState(""),
    [manifest, setManifest] = useState(
      JSON.stringify(app?.manifest ?? blank, null, 2),
    ),
    [scan, setScan] = useState<AppScan | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [fileName, setFileName] = useState("");
  const catalog = useQuery({
    queryKey: ["hub", "apps", "catalog"],
    queryFn: getAppCatalog,
  });
  const edit = (value: string) => {
    setManifest(value);
    setScan(null);
    setError("");
  };
  const pkg = (): AppPackage => {
    let parsed: AppManifest;
    try {
      parsed = JSON.parse(manifest) as AppManifest;
    } catch {
      throw new Error("manifest 不是有效的 JSON，请检查逗号与引号");
    }
    return { ...(app ? { id: app.id } : {}), html, manifest: parsed };
  };
  const run = async (publish = false) => {
    setBusy(true);
    setError("");
    try {
      if (publish) {
        if (!scan?.valid) throw new Error("请先完成检查");
        onSaved(await publishApp(pkg()));
      } else setScan(await validateApp(pkg()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败，请重试");
    } finally {
      setBusy(false);
    }
  };
  const toggleTool = (name: string) => {
    try {
      const m = pkg().manifest;
      m.tools = m.tools.includes(name)
        ? m.tools.filter((t) => t !== name)
        : [...m.tools, name];
      edit(JSON.stringify(m, null, 2));
    } catch {
      setError("请先修正 manifest JSON，再选择工具");
    }
  };
  const selected = (() => {
    try {
      return (JSON.parse(manifest) as AppManifest).tools ?? [];
    } catch {
      return [];
    }
  })();
  const fields = (() => {
    try {
      return JSON.parse(manifest) as AppManifest;
    } catch {
      return null;
    }
  })();
  const setField = (key: "name" | "description" | "version", value: string) => {
    if (!fields) {
      setError("请在高级配置中修正 manifest JSON");
      return;
    }
    edit(JSON.stringify({ ...fields, [key]: value }, null, 2));
  };
  return (
    <section
      className="apps-editor"
      aria-label={app ? "发布新版本" : "上传应用"}
    >
      <header>
        <div>
          <p className="apps-eyebrow">{app ? "新的私有版本" : "单文件应用"}</p>
          <h2>{app ? `更新「${app.name}」` : "先自己试，再分享"}</h2>
        </div>
        <button onClick={onClose} disabled={busy}>关闭上传</button>
      </header>
      <p>
        上传一个不超过 2 MiB 的
        HTML。检查通过后，发布为仅自己可用；全员共享需单独申请。
      </p>
      <div className="apps-basic-fields">
        <label>
          应用名称<input
            value={fields?.name ?? ""}
            disabled={busy || !fields}
            onChange={(e) => setField("name", e.target.value)}
            maxLength={80}
          />
        </label>
        <label>
          版本<input
            value={fields?.version ?? ""}
            disabled={busy || !fields}
            onChange={(e) => setField("version", e.target.value)}
          />
        </label>
        <label className="apps-basic-description">
          用途<input
            value={fields?.description ?? ""}
            disabled={busy || !fields}
            onChange={(e) => setField("description", e.target.value)}
          />
        </label>
      </div>
      <label className="apps-file">
        HTML 文件<input
          type="file"
          accept=".html,.htm,text/html"
          disabled={busy}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setScan(null);
            setError("");
            setHtml("");
            if (file.size > HTML_LIMIT) {
              setError("HTML 超过 2 MiB，请缩小文件");
              return;
            }
            try {
              const text = await file.text();
              setHtml(text);
              setFileName(file.name);
            } catch {
              setError("文件读取失败，请重新选择");
            }
          }}
        />
      </label>
      {fileName && (
        <p className="apps-meta">
          {fileName} · {new Blob([html]).size.toLocaleString()} 字节
        </p>
      )}
      <details className="apps-advanced">
        <summary>高级配置 · HTML 与 manifest</summary>
        <div className="apps-editor-grid">
          <label>
            HTML 内容<textarea
              aria-label="HTML 内容"
              value={html}
              spellCheck={false}
              disabled={busy}
              onChange={(e) => {
                setHtml(e.target.value);
                setScan(null);
              }}
              rows={15}
            />
          </label>
          <label>
            manifest · 名称、版本与申请范围<textarea
              aria-label="manifest JSON"
              value={manifest}
              onChange={(e) => edit(e.target.value)}
              disabled={busy}
              rows={15}
              spellCheck={false}
            />
          </label>
        </div>
      </details>
      <details>
        <summary>从数据工具目录选择权限</summary>
        <p className="apps-meta">
          L0/L1 且无外联或设备权限可自动共享。L2、外部域名或设备权限须 CEO
          审批。
        </p>
        {catalog.isPending
          ? <p>正在读取工具目录…</p>
          : catalog.error
          ? (
            <div role="alert">
              工具目录读取失败：{catalog.error.message}
              <button onClick={() => void catalog.refetch()}>
                重试读取目录
              </button>
            </div>
          )
          : (
            <div className="apps-tool-list">
              {catalog.data?.filter(isAppDataTool).map((t) => (
                <label key={t.name}>
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={selected.includes(t.name)}
                    onChange={() =>
                      toggleTool(t.name)}
                  />
                  <strong>{t.title ?? t.name}</strong>
                  {t.title && <code className="apps-tool-name">{t.name}</code>}
                  {" "}
                  · {t.sensitivity}
                  <small>{t.description}</small>
                </label>
              ))}
            </div>
          )}
      </details>
      {scan && (
        <section
          className={`apps-scan ${scan.valid ? "valid" : "invalid"}`}
          aria-label="检查结果"
        >
          <h3>{scan.valid ? "检查通过" : "检查未通过"}</h3>
          <p>
            {scan.size.toLocaleString()} 字节 · {scan.errors.length} 个错误 ·
            {" "}
            {scan.warnings.length} 个提醒
          </p>
          {scan.errors.map((f, i) => (
            <p role="alert" key={`error-${i}`}>
              {f.line ? `第 ${f.line} 行 · ` : ""}
              {f.message}
            </p>
          ))}
          {scan.warnings.map((f, i) => (
            <p key={`warn-${i}`}>
              提醒：{f.line ? `第 ${f.line} 行 · ` : ""}
              {f.message}
            </p>
          ))}
        </section>
      )}
      {error && <p role="alert">{error}。请修改后重试。</p>}
      <footer>
        <button onClick={() => void run()} disabled={busy || !html.trim()}>
          {busy ? "正在处理…" : "检查应用包"}
        </button>
        <button
          className="apps-primary"
          disabled={busy || !scan?.valid}
          onClick={() => void run(true)}
        >
          发布为仅自己可用
        </button>
      </footer>
    </section>
  );
}
